import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/api/auth/useCurrentUser";
import { updateFeedbackStatus } from "@/api/feedback.api";
import { toast } from "sonner";

interface FeedbackWebViewProps {
	onComplete: () => void;
	onBack: () => void;
}

export function FeedbackWebView({ onComplete, onBack }: FeedbackWebViewProps) {
	const { user } = useCurrentUser();
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const extraId = user?.extraId;
	const [isFormStarted, setIsFormStarted] = useState(false);
	const [showExitWarning, setShowExitWarning] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [isSurveyOpened, setIsSurveyOpened] = useState(false);

	// Listen for postMessage from the feedback form
	useEffect(() => {
		const handleMessage = async (event: MessageEvent) => {
			// Security: Only accept messages from survey.fraport.com
			if (!event.origin.includes("survey.fraport.com")) {
				return;
			}

			try {
				const data =
					typeof event.data === "string" ? JSON.parse(event.data) : event.data;

				// Track when user starts interacting with the form
				if (data.formStarted || data.pageChanged || data.questionAnswered) {
					setIsFormStarted(true);
				}

				// Check if interview is finished
				if (data.interviewIsFinished === true && extraId) {
					console.log("Feedback form completed, updating status...");

					// Update backend that feedback is completed
					await updateFeedbackStatus(extraId, true);

					toast.success("Feedback erfolgreich abgeschlossen!");

					// Add 1 second delay before closing to show success message
					setTimeout(() => {
						onComplete();
					}, 1000);
				}
			} catch (error) {
				console.error("Error handling feedback message:", error);
			}
		};

		// Add event listener
		window.addEventListener("message", handleMessage);

		// Cleanup
		return () => {
			window.removeEventListener("message", handleMessage);
		};
	}, [extraId, onComplete]);

	// Handle back button with warning if form is started
	const handleBackClick = () => {
		if (isFormStarted) {
			setShowExitWarning(true);
		} else {
			onBack();
		}
	};

	// Handle confirmed exit
	const handleConfirmExit = () => {
		setShowExitWarning(false);
		onBack();
	};

	// Handle cancel exit
	const handleCancelExit = () => {
		setShowExitWarning(false);
	};

	// Handle iframe load
	const handleIframeLoad = () => {
		// Check if iframe content actually loaded
		try {
			const iframe = iframeRef.current;
			if (iframe && iframe.contentDocument) {
				// If we can access content, it loaded successfully
				setIsLoading(false);
				console.log("Feedback iframe loaded successfully");
			} else {
				// Can't access content - likely blocked by X-Frame-Options
				setTimeout(() => {
					setIsLoading(false);
					setLoadError(
						"Das Feedback-Formular kann nicht eingebettet werden. Die Website blockiert die Anzeige in einem Frame."
					);
				}, 1000);
			}
		} catch (error) {
			// Cross-origin or other error
			setTimeout(() => {
				setIsLoading(false);
				setLoadError(
					"Das Feedback-Formular kann nicht eingebettet werden. Die Website blockiert die Anzeige in einem Frame."
				);
			}, 1000);
		}
	};

	// Handle iframe error
	const handleIframeError = () => {
		setIsLoading(false);
		setLoadError("Das Feedback-Formular ist derzeit nicht verfügbar");
		console.error("Failed to load feedback iframe");
	};

	// Add a timeout to detect if iframe doesn't load
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (isLoading) {
				setIsLoading(false);
				setLoadError(
					"Das Feedback-Formular lädt nicht. Die Website blockiert möglicherweise die Anzeige in einem Frame."
				);
			}
		}, 5000); // 5 second timeout

		return () => clearTimeout(timeout);
	}, [isLoading]);

	// Handle manual feedback completion (fallback)
	const handleManualComplete = async () => {
		if (!extraId) return;

		try {
			await updateFeedbackStatus(extraId, true);
			toast.success("Feedback als abgeschlossen markiert!");
			setTimeout(() => {
				onComplete();
			}, 1000);
		} catch (error) {
			console.error("Error updating feedback status:", error);
			toast.error("Fehler beim Aktualisieren des Feedback-Status");
		}
	};

	// Use the standard survey URL
	const feedbackUrl = "https://www.survey.fraport.com/hiljxttuyc?l=de";

	return (
		<div className="flex flex-col h-screen bg-gray-50">
			{/* Header */}
			<div className="bg-white shadow-sm border-b">
				<div className="px-4 py-3 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<Button
							variant="ghost"
							size="sm"
							onClick={handleBackClick}
							className="flex items-center gap-2"
						>
							<ArrowLeft className="w-4 h-4" />
							Zurück
						</Button>
						<h1 className="text-lg font-semibold text-gray-900">Feedback</h1>
					</div>
					<div className="flex items-center gap-2">
						<ExternalLink className="w-4 h-4 text-gray-500" />
						<span className="text-sm text-gray-500">survey.fraport.com</span>
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 p-4">
				<Card className="h-full">
					<CardHeader className="pb-4">
						<CardTitle className="text-center">
							Bitte füllen Sie das Feedback-Formular aus
						</CardTitle>
						<p className="text-sm text-gray-600 text-center">
							Das Formular wird automatisch abgeschlossen, wenn Sie alle Fragen
							beantwortet haben.
						</p>
					</CardHeader>
					<CardContent className="h-full pb-6">
						<div className="h-full bg-white rounded-lg border overflow-hidden relative">
							{isLoading && (
								<div className="absolute inset-0 flex items-center justify-center bg-gray-50">
									<div className="text-center">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
										<p className="text-sm text-gray-600">
											Feedback-Formular wird geladen...
										</p>
									</div>
								</div>
							)}
							{loadError && (
								<div className="absolute inset-0 flex items-center justify-center bg-gray-50">
									<div className="text-center max-w-md mx-auto p-6">
										<p className="text-sm text-gray-600 mb-6">
											Das Feedback-Formular kann hier nicht angezeigt werden.
											<br />
											Bitte öffnen Sie es in einem separaten Tab.
										</p>
										<div className="space-y-3">
											<Button
												onClick={() => {
													console.log("Opening survey URL:", feedbackUrl);
													window.open(feedbackUrl, "_blank");
													setIsSurveyOpened(true);
												}}
												className="w-full bg-[var(--ucm-primary-teal)] hover:bg-[var(--ucm-primary-teal-hover)] text-white"
											>
												Feedback-Formular öffnen
											</Button>
											{isSurveyOpened && (
												<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
													<p className="text-sm text-blue-700 mb-3 font-medium">
														Haben Sie das Feedback-Formular vollständig
														ausgefüllt?
													</p>
													<p className="text-xs text-blue-600 mb-3">
														Klicken Sie nur "Ja" wenn Sie alle Fragen
														beantwortet haben.
													</p>
													<Button
														onClick={handleManualComplete}
														className="w-full bg-[var(--ucm-primary-teal)] hover:bg-[var(--ucm-primary-teal-hover)] text-white"
													>
														✅ Ja, Feedback abgeschlossen
													</Button>
												</div>
											)}
										</div>
									</div>
								</div>
							)}
							<iframe
								ref={iframeRef}
								src={feedbackUrl}
								className="w-full h-full border-0"
								title="Feedback Form"
								allow="clipboard-write; web-share"
								loading="lazy"
								sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
								onLoad={handleIframeLoad}
								onError={handleIframeError}
							/>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Footer with instructions */}
			<div className="bg-white border-t p-4">
				<div className="text-center">
					<p className="text-sm text-gray-600">
						Beantworten Sie alle Fragen und das Formular wird automatisch
						abgeschlossen.
					</p>
					<p className="text-xs text-gray-500 mt-1">
						Bei Problemen verwenden Sie den "Zurück" Button oben links.
					</p>
				</div>
			</div>

			{/* Exit Warning Dialog */}
			<Dialog open={showExitWarning} onOpenChange={setShowExitWarning}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Feedback verlassen?</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<p className="text-sm text-gray-600">
							Sie haben bereits mit dem Ausfüllen des Feedback-Formulars
							begonnen. Wenn Sie jetzt verlassen, geht Ihr Fortschritt verloren
							und Sie müssen von vorne beginnen.
						</p>
						<p className="text-sm text-gray-600 mt-3 font-medium">
							Sind Sie sicher, dass Sie das Formular verlassen möchten?
						</p>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={handleCancelExit}>
							Abbrechen
						</Button>
						<Button variant="destructive" onClick={handleConfirmExit}>
							Ja, verlassen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
